// Wishlist Preferences v2 — Generic catalog multi-select component (Feb 2026).
// Shared logic for BrandMultiSelect and ProductCategoryMultiSelect.
//
// Renders:
//   • A search-input trigger that opens a Command popover with debounced
//     server-side search against /api/catalog/* endpoints.
//   • A chips list showing currently selected catalog entries with × buttons.
//   • An "Other" free-text input + chips for items NOT in the catalog.
//
// Contract (props):
//   • endpoint:        "/catalog/brands" | "/catalog/product-categories"
//   • value:           array of catalog slugs (controlled)
//   • customValue:     array of free-text strings (controlled)
//   • onChange(v):     fired with the new catalog slug array
//   • onCustomChange:  fired with the new custom string array
//   • renderLabel(it): fn (item) => string  — picks the right localised label
//   • filterParamKey:  "sport" | "group" — query string key for the optional filter
//   • filterValue:     current filter value ("" disables the filter)
//   • t:               translator
//   • testIdPrefix:    "wlp-brand" | "wlp-category"
//
// a11y: combobox role, search input, ↑↓/Enter/Esc keyboard navigation
// inherited from cmdk + Popover. Chips have explicit aria-label remove buttons.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const DEBOUNCE_MS = 250;

export const CatalogMultiSelect = ({
  endpoint,
  value = [],
  customValue = [],
  onChange,
  onCustomChange,
  renderLabel,
  filterParamKey,
  filterValue = "",
  t,
  testIdPrefix,
  label,
  placeholder,
  searchPlaceholder,
  searchEmpty,
  otherLabel,
  otherPlaceholder,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [otherInput, setOtherInput] = useState("");
  const debounceRef = useRef(null);

  // Debounce search input → debouncedQuery → triggers fetch.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  // Fetch catalog items whenever debounced query or filter changes (only when open).
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const params = { limit: 80 };
    if (debouncedQuery) params.q = debouncedQuery;
    if (filterValue && filterParamKey) params[filterParamKey] = filterValue;
    api
      .get(endpoint, { params })
      .then((r) => {
        if (!active) return;
        setItems(r.data?.items || []);
      })
      .catch(() => active && setItems([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, debouncedQuery, filterValue, filterParamKey, endpoint]);

  // Build a label lookup for already-selected slugs so chips show readable names
  // even when the dropdown hasn't been opened yet.
  const [labelCache, setLabelCache] = useState({});

  // Initial label hydration AND legacy auto-migration: when `value`
  // contains slugs we haven't seen, fetch the catalog once and:
  //   1. enrich `labelCache` for known slugs (chip labels);
  //   2. soft-migrate unknown legacy strings (free-text from the old CSV
  //      input) into `customValue` so user data isn't lost. This is an
  //      IN-MEMORY move: the user must press Save to persist. Idempotent
  //      — only fires when there are unknowns and the catalog has loaded.
  const [migrated, setMigrated] = useState(false);
  useEffect(() => {
    if (!value || value.length === 0) {
      setMigrated(true);
      return;
    }
    const missing = value.filter((k) => !labelCache[k]);
    if (missing.length === 0) return;
    let active = true;
    api
      .get(endpoint, { params: { limit: 200 } })
      .then((r) => {
        if (!active) return;
        const all = r.data?.items || [];
        const knownSlugs = new Set(all.map((it) => it.key));
        const updates = {};
        all.forEach((it) => {
          if (value.includes(it.key)) updates[it.key] = renderLabel(it);
        });
        if (Object.keys(updates).length > 0) {
          setLabelCache((prev) => ({ ...prev, ...updates }));
        }

        // Legacy free-text rescue.
        if (!migrated) {
          const legacy = value.filter((v) => !knownSlugs.has(v));
          if (legacy.length > 0) {
            const cleaned = value.filter((v) => knownSlugs.has(v));
            const mergedCustom = [...(customValue || [])];
            legacy.forEach((l) => {
              if (!mergedCustom.includes(l)) mergedCustom.push(l);
            });
            onChange(cleaned);
            onCustomChange(mergedCustom);
          }
          setMigrated(true);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, endpoint]);

  useEffect(() => {
    if (!value || value.length === 0) return;
    const missing = value.filter((k) => !labelCache[k]);
    if (missing.length === 0) return;
    // Each known item we just rendered enriches the cache.
    const updates = {};
    items.forEach((it) => {
      if (value.includes(it.key)) updates[it.key] = renderLabel(it);
    });
    if (Object.keys(updates).length > 0) {
      setLabelCache((prev) => ({ ...prev, ...updates }));
    }
  }, [items, value, renderLabel, labelCache]);

  const chipsLabels = useMemo(() => {
    const out = {};
    value.forEach((k) => {
      // Prefer the cache if we've seen the item; otherwise look at the
      // currently fetched items; finally fall back to the raw slug.
      if (labelCache[k]) {
        out[k] = labelCache[k];
        return;
      }
      const found = items.find((it) => it.key === k);
      out[k] = found ? renderLabel(found) : k;
    });
    return out;
  }, [value, items, labelCache, renderLabel]);

  const toggle = (slug) => {
    if (value.includes(slug)) {
      onChange(value.filter((v) => v !== slug));
    } else {
      onChange([...value, slug]);
    }
  };

  const removeChip = (slug) => onChange(value.filter((v) => v !== slug));
  const removeCustom = (txt) =>
    onCustomChange((customValue || []).filter((v) => v !== txt));

  const addOther = () => {
    const v = otherInput.trim();
    if (!v) return;
    if ((customValue || []).includes(v)) {
      setOtherInput("");
      return;
    }
    onCustomChange([...(customValue || []), v]);
    setOtherInput("");
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-wrapper`}>
      <label className="block text-sm">
        <span className="block text-slate-700 mb-1">{label}</span>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-label={label}
              className={cn(
                "w-full flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-start",
                "hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              )}
              data-testid={`${testIdPrefix}-trigger`}
            >
              <span className="text-slate-500 truncate">
                {value.length > 0
                  ? `${value.length} ${t("settings.wishlistV2.selectedCount") || "selected"}`
                  : placeholder}
              </span>
              <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
            align="start"
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
                <Search className="h-4 w-4 text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                  data-testid={`${testIdPrefix}-search`}
                  autoFocus
                />
              </div>
              <div className="max-h-72 overflow-y-auto py-1" role="listbox">
                {loading && (
                  <div className="px-3 py-3 text-sm text-slate-500" data-testid={`${testIdPrefix}-loading`}>
                    {t("common.loading") || "Loading..."}
                  </div>
                )}
                {!loading && items.length === 0 && (
                  <div className="px-3 py-3 text-sm text-slate-500" data-testid={`${testIdPrefix}-empty`}>
                    {searchEmpty}
                  </div>
                )}
                {!loading && items.length > 0 && items.map((it) => {
                  const selected = value.includes(it.key);
                  return (
                    <button
                      type="button"
                      key={it.key}
                      role="option"
                      aria-selected={selected}
                      onClick={() => toggle(it.key)}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 text-sm text-start hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none",
                        selected && "bg-emerald-50/50"
                      )}
                      data-testid={`${testIdPrefix}-option-${it.key}`}
                    >
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          selected ? "opacity-100 text-emerald-600" : "opacity-0"
                        )}
                      />
                      <span>{renderLabel(it)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </label>

      {/* Catalog chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {value.map((k) => (
            <Badge
              key={k}
              variant="secondary"
              className="bg-emerald-50 text-emerald-800 border border-emerald-200 ps-2 pe-1 py-0.5"
              data-testid={`${testIdPrefix}-chip-${k}`}
            >
              <span className="text-xs">{chipsLabels[k]}</span>
              <button
                type="button"
                onClick={() => removeChip(k)}
                aria-label={`Remove ${chipsLabels[k]}`}
                className="ms-1 rounded p-0.5 hover:bg-emerald-100"
                data-testid={`${testIdPrefix}-chip-${k}-remove`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* "Other" free-text section — separate, always visible. */}
      <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/60 p-2 space-y-2">
        <div className="text-xs text-slate-500">{otherLabel}</div>
        <div className="flex gap-2">
          <Input
            value={otherInput}
            onChange={(e) => setOtherInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOther();
              }
            }}
            placeholder={otherPlaceholder}
            maxLength={80}
            className="h-8 text-sm"
            data-testid={`${testIdPrefix}-other-input`}
          />
          <button
            type="button"
            onClick={addOther}
            disabled={!otherInput.trim()}
            className="px-3 h-8 rounded-md bg-slate-900 text-white text-xs disabled:opacity-40"
            data-testid={`${testIdPrefix}-other-add`}
          >
            +
          </button>
        </div>
        {(customValue || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-other-chips`}>
            {(customValue || []).map((c) => (
              <Badge
                key={c}
                variant="secondary"
                className="bg-amber-50 text-amber-800 border border-amber-200 ps-2 pe-1 py-0.5"
                data-testid={`${testIdPrefix}-other-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="text-xs">{c}</span>
                <button
                  type="button"
                  onClick={() => removeCustom(c)}
                  aria-label={`Remove ${c}`}
                  className="ms-1 rounded p-0.5 hover:bg-amber-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogMultiSelect;
